<template>
  <v-navigation-drawer
    :width="drawerTotalWidth"
    app
    permanent
    fixed
    :style="
      draggingChatWidth !== null
        ? 'transition: none;'
        : 'transition: width 0.2s cubic-bezier(0.4, 0, 0.2, 1);'
    "
  >
    <!-- Inner flex layout: nav column + optional chat column -->
    <div style="display: flex; height: 100%; overflow: hidden;">
      <!-- ── NAV COLUMN ────────────────────────────────────── -->
      <div
        class="nav-column"
        :style="{ width: navWidth + 'px', minWidth: navWidth + 'px' }"
        style="display: flex; flex-direction: column; overflow: hidden; border-right: 1px solid #e0e0e0;"
      >
        <!-- POD-Vis Logo -->
        <v-list class="pt-3" style="flex-shrink: 0;">
          <v-list-item>
            <div v-if="showExpanded()">
              <img
                width="100%"
                src="/images/POD-Vis_tag2.jpg"
                alt="POD-Vis: Probing Outcomes Data with Visual analytics"
              />
            </div>
            <div v-else>
              <img
                width="100%"
                src="/images/POD-Vis.jpg"
                alt="POD-Vis: Probing Outcomes Data with Visual analytics"
              />
            </div>
          </v-list-item>

          <v-list-item class="justify-center">
            <div class="font-weight-medium primary--text">
              v{{ podvis_version }}
            </div>
          </v-list-item>

          <!-- Application links -->
          <v-list-item
            v-for="item in menuItems"
            :key="item.title"
            :to="item.path"
            justify="center"
          >
            <!-- icon with description in tooltip -->
            <v-tooltip v-if="showExpanded()" color="primary" right>
              <template v-slot:activator="{ on: tooltip }">
                <v-list-item-action
                  class="pa-0 ma-0 pr-2 pb-2"
                  v-on="{ ...tooltip }"
                >
                  <v-icon color="primary" large class="pr-2 pt-1">{{
                    item.icon
                  }}</v-icon>
                </v-list-item-action>
              </template>
              <span class="subtitle-1">{{ item.descr }}</span>
            </v-tooltip>

            <!-- icon with name in tooltip -->
            <v-tooltip v-else color="primary" right>
              <template v-slot:activator="{ on: tooltip }">
                <v-list-item-action v-on="{ ...tooltip }">
                  <v-icon color="primary" large>{{ item.icon }}</v-icon>
                </v-list-item-action>
              </template>
              <span class="subtitle-1">{{
                item.name + ': ' + item.descr
              }}</span>
            </v-tooltip>

            <v-list-item-content v-if="showExpanded()" class="primary--text">
              <v-tooltip color="primary" right>
                <template v-slot:activator="{ on: tooltip }">
                  <span class="subtitle-1" v-on="{ ...tooltip }">{{
                    item.name
                  }}</span>
                </template>
                <span class="subtitle-1">{{ item.descr }}</span>
              </v-tooltip>
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <!-- Spacer to push bottom items down -->
        <div style="flex: 1;"></div>

        <!-- Bottom items -->
        <v-list style="flex-shrink: 0;">
          <!-- minimize/expand -->
          <v-list-item class="justify-center" @click="expand = !expand">
            <v-list-item-action
              v-if="showExpanded()"
              class="pa-0 ma-0 pr-2 pb-2"
            >
              <v-icon color="primary" x-large class="pr-2 pt-2"
                >chevron_left</v-icon
              >
            </v-list-item-action>

            <v-list-item-action v-else class="pa-0 ma-0">
              <v-tooltip color="primary" right>
                <template v-slot:activator="{ on: tooltip }">
                  <v-icon color="primary" x-large v-on="{ ...tooltip }"
                    >chevron_right</v-icon
                  >
                </template>
                <span class="subtitle-1">Expand menu</span>
              </v-tooltip>
            </v-list-item-action>

            <v-list-item-content v-if="showExpanded()" class="primary--text">
              <span class="subtitle-1">Minimize menu</span>
            </v-list-item-content>
          </v-list-item>

          <!-- chat toggle -->
          <v-list-item
            class="justify-center"
            :input-value="chatPanelOpen"
            @click="toggleChat"
          >
            <v-tooltip v-if="showExpanded()" color="primary" right>
              <template v-slot:activator="{ on: tooltip }">
                <v-list-item-action
                  class="pa-0 ma-0 pr-2 pb-2"
                  v-on="{ ...tooltip }"
                >
                  <v-badge :value="hasUnreadActivity" dot color="red" overlap>
                    <v-icon color="primary" large class="pr-2 pt-2"
                      >mdi-chat-outline</v-icon
                    >
                  </v-badge>
                </v-list-item-action>
              </template>
              <span class="subtitle-1"
                >Discuss your datasets' cohorts, analyses, and more.</span
              >
            </v-tooltip>
            <v-tooltip v-else color="primary" right>
              <template v-slot:activator="{ on: tooltip }">
                <v-list-item-action class="pa-0 ma-0" v-on="{ ...tooltip }">
                  <v-badge :value="hasUnreadActivity" dot color="red" overlap>
                    <v-icon color="primary" large>mdi-chat-outline</v-icon>
                  </v-badge>
                </v-list-item-action>
              </template>
              <span class="subtitle-1"
                >Team Chat: Discuss your datasets' cohorts, analyses, and
                more.</span
              >
            </v-tooltip>
            <v-list-item-content v-if="showExpanded()" class="primary--text">
              <v-tooltip color="primary" right>
                <template v-slot:activator="{ on: tooltip }">
                  <span class="subtitle-1" v-on="{ ...tooltip }"
                    >Team Chat</span
                  >
                </template>
                <span class="subtitle-1"
                  >Discuss your datasets' cohorts, analyses, and more.</span
                >
              </v-tooltip>
            </v-list-item-content>
          </v-list-item>

          <!-- notifications -->
          <v-list-item class="justify-center" @click="feedDialog = true">
            <v-tooltip v-if="showExpanded()" color="primary" right>
              <template v-slot:activator="{ on: tooltip }">
                <v-list-item-action
                  class="pa-0 ma-0 pr-2 pb-2"
                  v-on="{ ...tooltip }"
                >
                  <v-badge
                    :content="directedCount"
                    :value="hasUnreadActivity"
                    :dot="directedCount === 0"
                    color="red"
                    overlap
                  >
                    <v-icon color="primary" large class="pr-2 pt-2"
                      >mdi-bell-outline</v-icon
                    >
                  </v-badge>
                </v-list-item-action>
              </template>
              <span class="subtitle-1"
                >View and navigate to recent activity.</span
              >
            </v-tooltip>
            <v-tooltip v-else color="primary" right>
              <template v-slot:activator="{ on: tooltip }">
                <v-list-item-action class="pa-0 ma-0" v-on="{ ...tooltip }">
                  <v-badge
                    :content="directedCount"
                    :value="hasUnreadActivity"
                    :dot="directedCount === 0"
                    color="red"
                    overlap
                  >
                    <v-icon color="primary" large>mdi-bell-outline</v-icon>
                  </v-badge>
                </v-list-item-action>
              </template>
              <span class="subtitle-1"
                >Notifications: View and navigate to recent activity.</span
              >
            </v-tooltip>
            <v-list-item-content v-if="showExpanded()" class="primary--text">
              <v-tooltip color="primary" right>
                <template v-slot:activator="{ on: tooltip }">
                  <span class="subtitle-1" v-on="{ ...tooltip }"
                    >Notifications</span
                  >
                </template>
                <span class="subtitle-1"
                  >View and navigate to recent activity.</span
                >
              </v-tooltip>
            </v-list-item-content>
          </v-list-item>

          <!-- sign out -->
          <v-list-item class="justify-center" @click="signOutDialog = true">
            <v-list-item-action
              v-if="showExpanded()"
              class="pa-0 ma-0 pr-2 pb-2"
            >
              <v-icon color="primary" large class="pr-2 pt-2">close</v-icon>
            </v-list-item-action>
            <v-tooltip v-else color="primary" right>
              <template v-slot:activator="{ on: tooltip }">
                <v-list-item-action class="pa-0 ma-0" v-on="{ ...tooltip }">
                  <v-icon color="primary" large>close</v-icon>
                </v-list-item-action>
              </template>
              <span class="subtitle-1">Sign Out {{ user.name }}</span>
            </v-tooltip>
            <v-list-item-content v-if="showExpanded()" class="primary--text">
              <span class="subtitle-1"
                >Sign out<br clear="both" />{{ user.name }}</span
              >
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </div>

      <!-- ── CHAT COLUMN ─────────────────────────────────────── -->
      <template v-if="chatPanelOpen">
        <div style="flex: 1; overflow: hidden; min-width: 0;">
          <chat-panel></chat-panel>
        </div>

        <!-- Resize handle on the right edge of the chat column -->
        <div class="resize-handle" @mousedown.prevent="startResize"></div>
      </template>
    </div>

    <!-- ACTIVITY FEED DIALOG -->
    <v-dialog v-model="feedDialog" width="600">
      <v-card>
        <v-card-title class="primary--text d-flex justify-space-between">
          <span>Notifications</span>
          <div>
            <v-btn
              v-if="hasUnreadActivity"
              small
              text
              color="primary"
              class="mr-1"
              @click="clearFeed"
              >Mark all read</v-btn
            >
            <v-btn icon @click="feedDialog = false"
              ><v-icon>close</v-icon></v-btn
            >
          </div>
        </v-card-title>
        <v-card-subtitle
          >Recent messages from others across your study
          datasets</v-card-subtitle
        >
        <v-divider></v-divider>
        <v-card-text style="max-height: 60vh; overflow-y: auto;" class="pa-0">
          <v-list v-if="unreadNotifications.length > 0" two-line>
            <template v-for="(msg, idx) in unreadNotifications">
              <v-list-item
                :key="msg.id"
                class="py-1 notification-link"
                :style="
                  isDirected(msg) ? 'background: var(--tag-channel-bg);' : ''
                "
                @click="navigateToNotification(msg)"
              >
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    <span class="primary--text">{{ msg.user }}</span>
                    <span class="caption grey--text ml-2">
                      in
                      {{
                        msg.channel_type === 'direct' ||
                        msg.channel_type === 'group_dm'
                          ? ''
                          : '#'
                      }}{{ msg.channel_display_name }} · {{ msg.collection }}
                    </span>
                  </v-list-item-title>
                  <v-list-item-subtitle
                    class="text-wrap"
                    style="white-space: normal;"
                    >{{
                      resolveContent(msg).length > 120
                        ? resolveContent(msg).slice(0, 120) + '…'
                        : resolveContent(msg)
                    }}</v-list-item-subtitle
                  >
                  <v-list-item-subtitle class="caption grey--text"
                    >{{ formatDateDistance(msg.date_generated)
                    }}<span v-if="msg.date_generated !== msg.last_modified">
                      · edited</span
                    ></v-list-item-subtitle
                  >
                </v-list-item-content>
              </v-list-item>
              <v-divider
                v-if="idx < unreadNotifications.length - 1"
                :key="'d-' + msg.id"
              ></v-divider>
            </template>
          </v-list>
          <div v-else class="text-center grey--text py-8">
            No new notifications.
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- SIGN OUT DIALOG -->
    <v-dialog v-model="signOutDialog" width="500" persistent>
      <v-card>
        <v-card-title color="white" primary-title>
          <v-icon color="primary darken-3">warning</v-icon>
          <span class="primary--text text--darken-3 title pl-2">Sign Out</span>
        </v-card-title>
        <v-card-text class="primary primary--text text--lighten-5 pt-4"
          >Are you sure you'd like to sign out?</v-card-text
        >
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="signOutDialog = false"
            ><v-icon left>close</v-icon>Cancel</v-btn
          >
          <v-btn color="primary" @click="signUserOut">Sign Out</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-navigation-drawer>
</template>

<script>
import axios from 'axios';
import { createNamespacedHelpers, mapState } from 'vuex';
import { actions as authActions, state } from '@/store/modules/auth/types';
import {
  actions as collaborationActions,
  getters as collaborationGetters,
} from '@/store/modules/collaboration/types';
import { formatDistance } from 'date-fns';
import resolveTagsToText from '@/utils/resolveTags';
import ChatPanel from './ChatPanel.vue';

const { mapActions } = createNamespacedHelpers('auth');

const FEED_POLL_INTERVAL_MS = 20000;
const NAV_WIDTH_EXPANDED = 256;
const NAV_WIDTH_MINI = 100;

export default {
  components: { ChatPanel },
  data() {
    return {
      signOutDialog: false,
      feedDialog: false,
      expand: true,
      feedPollIntervalId: null,
      draggingChatWidth: null,
      menuItems: [
        {
          name: 'Home Page',
          icon: 'home',
          path: '/homepage',
          descr: 'Return to the POD-Vis home page.',
        },
        {
          name: 'Create Study Dataset',
          icon: 'library_add',
          path: '/datasets',
          descr: 'Begin a new analysis by creating a study dataset.',
        },
        {
          name: 'Saved Study Datasets',
          icon: 'library_books',
          path: '/study_datasets',
          descr: 'Review previous analyses and study datasets.',
        },
      ],
    };
  },
  computed: {
    ...mapState('auth', {
      user: state.USER,
    }),
    chatPanelOpen() {
      return this.$store.getters[
        'collaboration/' + collaborationGetters.CHAT_PANEL_OPEN
      ];
    },
    chatPanelWidth() {
      return this.$store.getters[
        'collaboration/' + collaborationGetters.CHAT_PANEL_WIDTH
      ];
    },
    navWidth() {
      return (this.showExpanded() ? NAV_WIDTH_EXPANDED : NAV_WIDTH_MINI) + 1;
    },
    drawerTotalWidth() {
      const chatWidth =
        this.draggingChatWidth !== null
          ? this.draggingChatWidth
          : this.chatPanelWidth;
      return this.navWidth + (this.chatPanelOpen ? 6 + chatWidth : 0);
    },
    channelsByCollection() {
      return (
        this.$store.getters[
          'collaboration/' + collaborationGetters.CHANNELS_BY_COLLECTION
        ] || {}
      );
    },
    usersById() {
      return (
        this.$store.getters[
          'collaboration/' + collaborationGetters.USERS_BY_ID
        ] || {}
      );
    },
    feed() {
      return (
        this.$store.getters['collaboration/' + collaborationGetters.FEED] || []
      );
    },
    unreadFeed() {
      return (
        this.$store.getters[
          'collaboration/' + collaborationGetters.UNREAD_FEED
        ] || []
      );
    },
    unreadNotifications() {
      const userId = this.user ? this.user.id : null;
      return this.unreadFeed.filter(m => m.user_id !== userId);
    },
    directedCount() {
      const userId = this.user ? this.user.id : null;
      if (!userId) return 0;
      return this.unreadNotifications.filter(m => this.isDirected(m)).length;
    },
    hasUnreadActivity() {
      return this.unreadNotifications.length > 0;
    },
    podvis_version() {
      return process.env.PODVIS_VERSION;
    },
  },
  watch: {
    expand(value) {
      if (window.localStorage.expand != value) {
        window.localStorage.expand = value;
      }
    },
  },
  created() {
    if ('expand' in window.localStorage) {
      const { expand } = window.localStorage;
      this.expand = eval(expand);
    } else {
      this.expand = true;
    }
    this.$store.dispatch('collaboration/' + collaborationActions.FETCH_FEED);
    this.feedPollIntervalId = setInterval(() => {
      this.$store.dispatch('collaboration/' + collaborationActions.FETCH_FEED);
    }, FEED_POLL_INTERVAL_MS);

    axios.get('/api/features_enabled').then(({ data }) => {
      if (data && data['data_preview']) {
        this.menuItems.push({
          name: 'Data Preview',
          icon: 'mdi-chart-timeline',
          path: '/data_preview',
          descr:
            'Preview/visualize the subject visit data in a tab or comma-separated flat file.',
        });
      }
      if (data && data['individual_audiogram']) {
        this.menuItems.push({
          name: 'Patient Audiogram',
          icon: 'mdi-ear-hearing',
          path: '/patient_audiogram',
          descr:
            'Display individual patient audiograms alongside user-defined study groups.',
        });
      }
      if (data && data['admin_interface']) {
        this.menuItems.push({
          name: 'Admin Console',
          icon: 'mdi-account-supervisor',
          path: '/admin',
          descr: 'Administer users, groups, and uploaded dataset permissions.',
        });
      }
    });
  },
  beforeDestroy() {
    if (this.feedPollIntervalId !== null) {
      clearInterval(this.feedPollIntervalId);
    }
    document.removeEventListener('mousemove', this._resizeMouseMove);
    document.removeEventListener('mouseup', this._resizeMouseUp);
  },
  methods: {
    ...mapActions({
      signUserOut: authActions.SIGN_USER_OUT,
    }),
    isDirected(msg) {
      const userId = this.user ? this.user.id : null;
      if (!userId) return false;
      return (
        msg.channel_type === 'direct' ||
        msg.channel_type === 'group_dm' ||
        (msg.content && msg.content.includes(`@user:${userId}`))
      );
    },
    showExpanded() {
      return this.expand;
    },
    resolveContent(msg) {
      const channels = this.channelsByCollection[msg.collection_id] || [];
      const text = resolveTagsToText(msg.content, channels, this.usersById);
      const hasImage = msg.attachments && msg.attachments.length > 0;
      if (hasImage && text) {
        return 'Sent an image: ' + text;
      }
      if (hasImage) {
        return 'Sent an image';
      }
      return text;
    },
    clearFeed() {
      this.$store.dispatch(
        'collaboration/' + collaborationActions.MARK_ALL_READ
      );
      this.feedDialog = false;
    },
    navigateToNotification(msg) {
      this.feedDialog = false;
      this.$store.dispatch(
        'collaboration/' + collaborationActions.NAVIGATE_TO_MESSAGE,
        {
          collectionId: msg.collection_id,
          collectionLabel: msg.collection,
          channelName: msg.channel,
          messageId: msg.id,
        }
      );
    },
    formatDateDistance(d) {
      if (!d) return '';
      return formatDistance(new Date(d), new Date(), { addSuffix: true });
    },
    toggleChat() {
      if (this.chatPanelOpen) {
        this.$store.dispatch(
          'collaboration/' + collaborationActions.CLOSE_CHAT
        );
      } else {
        this.$store.dispatch('collaboration/' + collaborationActions.OPEN_CHAT);
      }
    },
    startResize(e) {
      const startX = e.clientX;
      const startWidth = this.chatPanelWidth;

      this._resizeMouseMove = ev => {
        const delta = ev.clientX - startX;
        this.draggingChatWidth = Math.min(
          Math.max(startWidth + delta, 240),
          900
        );
      };

      this._resizeMouseUp = () => {
        if (this.draggingChatWidth !== null) {
          this.$store.dispatch(
            'collaboration/' + collaborationActions.SET_CHAT_WIDTH,
            this.draggingChatWidth
          );
          this.draggingChatWidth = null;
        }
        document.removeEventListener('mousemove', this._resizeMouseMove);
        document.removeEventListener('mouseup', this._resizeMouseUp);
      };

      document.addEventListener('mousemove', this._resizeMouseMove);
      document.addEventListener('mouseup', this._resizeMouseUp);
    },
  },
};
</script>

<style scoped>
.nav-column {
  flex-shrink: 0;
}

.resize-handle {
  width: 6px;
  flex-shrink: 0;
  cursor: col-resize;
  background: transparent;
  border-left: 2px solid #e0e0e0;
  transition: border-color 0.15s;
  z-index: 1;
}

.resize-handle:hover,
.resize-handle:active {
  border-left-color: var(--v-primary-base);
}

.notification-link {
  cursor: pointer;
}
.notification-link:hover {
  background: rgba(0, 0, 0, 0.04) !important;
}
</style>
